import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Bell, Search, Sun, Moon, Menu, LogOut, User, Shield, Building2, Check, Trash2, ChevronsUpDown,
} from "lucide-react";
import { NAV_ITEMS } from "@/lib/nav";

export function Topbar({ onOpenSidebar }) {
  const { user, logout, impersonating, stopImpersonation } = useAuth();
  const { theme, toggle } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const qc = useQueryClient();
  const [search, setSearch] = useState("");
  const isSuper = user?.role === "super_admin";

  const { data: notifData } = useQuery({
    queryKey: ["notifications"],
    queryFn: async () => (await api.get("/notifications")).data.data,
    enabled: !isSuper || !!impersonating,
    refetchInterval: 60000,
  });
  const notifications = notifData?.notifications || [];
  const unread = notifData?.unread || 0;

  const markAll = async () => { await api.post("/notifications/read-all"); qc.invalidateQueries({ queryKey: ["notifications"] }); };
  const markRead = async (id) => { await api.patch(`/notifications/${id}/read`); qc.invalidateQueries({ queryKey: ["notifications"] }); };
  const removeNotif = async (id) => { await api.delete(`/notifications/${id}`); qc.invalidateQueries({ queryKey: ["notifications"] }); };

  const suggestions = search
    ? NAV_ITEMS.filter((i) => i.label.toLowerCase().includes(search.toLowerCase()))
    : [];

  const initials = `${user?.first_name?.[0] || ""}${user?.last_name?.[0] || ""}`.toUpperCase();

  return (
    <header className="sticky top-0 z-30 flex h-16 items-center gap-3 border-b border-border bg-background/80 px-4 backdrop-blur-xl md:px-6" data-testid="topbar">
      <Button variant="ghost" size="icon" className="lg:hidden" onClick={onOpenSidebar} data-testid="open-sidebar-btn">
        <Menu className="h-5 w-5" />
      </Button>

      <div className="relative hidden max-w-md flex-1 md:block">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          data-testid="global-search"
          placeholder="Cerca nella piattaforma..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="rounded-full border-border bg-muted/50 pl-9"
        />
        {suggestions.length > 0 && (
          <div className="absolute mt-2 w-full overflow-hidden rounded-xl border border-border bg-popover shadow-lg">
            {suggestions.slice(0, 6).map((s) => (
              <button
                key={s.to}
                className="flex w-full items-center gap-2 px-4 py-2.5 text-left text-sm hover:bg-accent"
                onClick={() => { navigate(s.to); setSearch(""); }}
              >
                <s.icon className="h-4 w-4 text-muted-foreground" /> {s.label}
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="ml-auto flex items-center gap-1.5">
        {impersonating && (
          <Badge variant="outline" className="mr-1 hidden gap-1.5 border-amber-500/40 bg-amber-500/10 text-amber-600 sm:flex" data-testid="impersonation-badge">
            <Shield className="h-3 w-3" /> {impersonating.tenant_name}
            <button onClick={stopImpersonation} className="ml-1 underline">Esci</button>
          </Badge>
        )}

        {/* Tenant switcher */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="hidden gap-2 rounded-full text-sm font-medium md:flex" data-testid="tenant-switcher">
              <Building2 className="h-4 w-4 text-primary" />
              <span className="max-w-[120px] truncate">{impersonating?.tenant_name || (isSuper ? "Piattaforma" : "Azienda")}</span>
              <ChevronsUpDown className="h-3.5 w-3.5 text-muted-foreground" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuLabel>Azienda corrente</DropdownMenuLabel>
            <DropdownMenuItem disabled>
              <Building2 className="mr-2 h-4 w-4" /> {impersonating?.tenant_name || "Il tuo tenant"}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        <Button variant="ghost" size="icon" onClick={toggle} data-testid="theme-toggle" className="rounded-full">
          {theme === "dark" ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
        </Button>

        <Popover>
          <PopoverTrigger asChild>
            <Button variant="ghost" size="icon" className="relative rounded-full" data-testid="notifications-btn">
              <Bell className="h-5 w-5" />
              {unread > 0 && (
                <span className="absolute right-1.5 top-1.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1 text-[10px] font-semibold text-primary-foreground" data-testid="notification-badge">
                  {unread}
                </span>
              )}
            </Button>
          </PopoverTrigger>
          <PopoverContent align="end" className="w-80 p-0" data-testid="notifications-panel">
            <div className="flex items-center justify-between border-b border-border px-4 py-3">
              <span className="font-heading font-semibold">Notifiche</span>
              {unread > 0 && (
                <button onClick={markAll} className="text-xs text-primary hover:underline" data-testid="mark-all-read">
                  Segna tutte lette
                </button>
              )}
            </div>
            <ScrollArea className="max-h-80">
              {notifications.length === 0 ? (
                <p className="px-4 py-8 text-center text-sm text-muted-foreground">Nessuna notifica</p>
              ) : (
                notifications.map((n) => (
                  <div key={n.id} className={`group flex gap-3 border-b border-border/60 px-4 py-3 ${!n.read ? "bg-primary/5" : ""}`} data-testid="notification-item">
                    <div className="flex-1">
                      <p className="text-sm font-medium">{n.title}</p>
                      <p className="text-xs text-muted-foreground">{n.body}</p>
                    </div>
                    <div className="flex flex-col gap-1 opacity-0 transition-opacity group-hover:opacity-100">
                      {!n.read && <button onClick={() => markRead(n.id)} title="Segna letta"><Check className="h-3.5 w-3.5 text-primary" /></button>}
                      <button onClick={() => removeNotif(n.id)} title="Elimina"><Trash2 className="h-3.5 w-3.5 text-muted-foreground" /></button>
                    </div>
                  </div>
                ))
              )}
            </ScrollArea>
          </PopoverContent>
        </Popover>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="ml-1 flex items-center gap-2 rounded-full outline-none ring-primary focus-visible:ring-2" data-testid="profile-menu">
              <Avatar className="h-9 w-9 border border-border">
                <AvatarImage src={user?.avatar_url} />
                <AvatarFallback className="bg-primary/10 text-primary text-sm font-semibold">{initials || "?"}</AvatarFallback>
              </Avatar>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuLabel>
              <p className="font-medium">{user?.first_name} {user?.last_name}</p>
              <p className="text-xs font-normal text-muted-foreground">{user?.email}</p>
              <Badge variant="secondary" className="mt-1.5">{user?.role_label}</Badge>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            {!isSuper && (
              <>
                <DropdownMenuItem onClick={() => navigate("/settings/profile")} data-testid="menu-profile">
                  <User className="mr-2 h-4 w-4" /> Profilo
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/settings/security")} data-testid="menu-security">
                  <Shield className="mr-2 h-4 w-4" /> Sicurezza
                </DropdownMenuItem>
              </>
            )}
            {isSuper && (
              <DropdownMenuItem onClick={() => navigate("/admin")} data-testid="menu-admin">
                <Shield className="mr-2 h-4 w-4" /> Console Admin
              </DropdownMenuItem>
            )}
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={logout} className="text-destructive focus:text-destructive" data-testid="logout-btn">
              <LogOut className="mr-2 h-4 w-4" /> Esci
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}
